import Api from "../../../utils/Api";
import { BASE_API_URL, DYNAMIC_QR } from "../../../constants/api-path.c";
import { BAD_REQUEST_PAGE, SERVER_ERROR_PAGE } from "../../../constants/path.c";
import {
  QRPH_AMOUNT_POPUP,
  QRPH_DEFAULTS,
  QRPH_REFERENCE,
} from "../../../constants/qrph.c";
import { getLanguagePrefix } from "../../hooks/getLanguagePrefix";

export type QrphQrResult = {
  qrImage: string;
  qrString?: string;
  amount?: string;
  currency?: string;
  merchantName?: string;
  reference?: string;
  transactionID?: string;
  uuid?: string;
};

type UseQrphQrArgs = {
  apiData: any;
  slug: string;
  setDasLoader: (v: boolean) => void;
  setAmountPopup: (fn: any) => void;
  onQrGenerated: (qr: QrphQrResult) => void;
  navigate: any;
};

/**
 * QR generation flow for the Qrph button: builds the payload, calls the
 * generate-qr API and hands the result to onQrGenerated, or redirects to the
 * matching error page. requestQr() opens the amount popup first when the
 * payment has no amount yet.
 */
export const useQrphQr = ({
  apiData,
  slug,
  setDasLoader,
  setAmountPopup,
  onQrGenerated,
  navigate,
}: UseQrphQrArgs) => {
  const redirectToErrorPage = (result: any) => {
    let redirectURL = `/${BAD_REQUEST_PAGE}`;
    if (result?.success === false) {
      redirectURL = `/${SERVER_ERROR_PAGE}/${result?.Ref || ""}`;
    }
    navigate(`${getLanguagePrefix()}${redirectURL}`);
  };

  const generateQr = async () => {
    setDasLoader(true);
    try {
      localStorage.setItem("slug", slug);
      const reference = (slug || "")
        .replace(QRPH_REFERENCE.SANITIZE_REGEX, "")
        .slice(0, QRPH_REFERENCE.MAX_LENGTH);
      const merchantName = apiData.company || QRPH_DEFAULTS.MERCHANT_NAME;
      const amount = String(apiData.amount);

      const payload = {
        URLKey: slug,
        amount,
        currency: apiData.currency,
        merchantName,
        proxyType: QRPH_DEFAULTS.PROXY_TYPE,
        proxyValue: QRPH_DEFAULTS.PROXY_VALUE,
        reference,
        isDynamic: true,
        amountEditable: false,
      };

      const data = await Api().post(
        `${BASE_API_URL}/${DYNAMIC_QR.GENERATE}/qrph`,
        payload
      );

      const qrImage = data?.qrImage || data?.data?.qrImage;
      if (!qrImage) {
        redirectToErrorPage(data);
        return;
      }

      onQrGenerated({
        qrImage,
        qrString: data?.qrString || data?.data?.qrString,
        amount,
        currency: apiData.currency,
        merchantName,
        reference,
        transactionID: data?.transactionID || data?.data?.transactionID,
        uuid:
          data?.uuid ||
          data?.data?.uuid ||
          data?.transactionId ||
          data?.data?.transactionId,
      });
      setAmountPopup((prev: any) => ({ ...prev, open: false }));
    } catch (error) {
      redirectToErrorPage(undefined);
    } finally {
      setDasLoader(false);
    }
  };

  const requestQr = () => {
    if (!apiData.amount) {
      setAmountPopup((prev: any) => ({
        ...prev,
        open: true,
        logo: QRPH_AMOUNT_POPUP.LOGO,
        type: QRPH_AMOUNT_POPUP.TYPE,
      }));
      return;
    }
    generateQr();
  };

  return { generateQr, requestQr };
};
